import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft,
  Truck,
  Fuel,
  Wrench,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  FileText,
  ClipboardCheck,
  Star,
} from 'lucide-react';
import { toast } from 'sonner';
import AddCostDialog from '../components/AddCostDialog';
import EditCostDialog from '../components/EditCostDialog';
import DriverScorecard from '../components/DriverScorecard';
import VehicleCostsTab from '../components/VehicleCostsTab';
import VehicleDocumentsTab from '../components/VehicleDocumentsTab';
import VehicleInspectionsTab from '../components/VehicleInspectionsTab';

// Format Rand: R 1,234.56
const fmtR = (v) => {
  if (v == null || isNaN(v)) return 'R 0.00';
  const n = Number(v);
  const parts = n.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `R ${parts.join('.')}`;
};

const VehicleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Core state
  const [vehicle, setVehicle] = useState(null);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('costs');

  // Costs state
  const [costFilter, setCostFilter] = useState('all');
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [editCostDialogOpen, setEditCostDialogOpen] = useState(false);
  const [selectedCost, setSelectedCost] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceOdometer, setServiceOdometer] = useState('');
  const [markingServiced, setMarkingServiced] = useState(false);

  // Mark-as-sold modal state
  const [soldDialogOpen, setSoldDialogOpen] = useState(false);
  const [soldDate, setSoldDate] = useState('');
  const [soldPrice, setSoldPrice] = useState('');
  const [soldBuyer, setSoldBuyer] = useState('');
  const [savingSold, setSavingSold] = useState(false);

  const markSold = async () => {
    if (!soldDate) { toast.error('Pick the sale date'); return; }
    setSavingSold(true);
    try {
      const updated = await api.markVehicleSold(id, {
        sold_date: soldDate,
        sold_price: soldPrice ? parseFloat(soldPrice) : undefined,
        sold_buyer: soldBuyer || undefined,
      });
      setVehicle(updated);
      setSoldDialogOpen(false);
      setSoldDate(''); setSoldPrice(''); setSoldBuyer('');
      toast.success(`${updated.name} marked as sold`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark as sold');
    } finally { setSavingSold(false); }
  };

  const unmarkSold = async () => {
    if (!window.confirm('Return this vehicle to active? Future costs, forecasts and alerts will track again.')) return;
    try {
      const updated = await api.unmarkVehicleSold(id);
      setVehicle(updated);
      toast.success(`${updated.name} reactivated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unmark');
    }
  };

  // Documents & Inspections state (managed by sub-components)
  const [documents, setDocuments] = useState([]);
  const [inspections, setInspections] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [vehicleData, costsData, docsData, inspData] = await Promise.all([
        api.getVehicle(id),
        api.getCosts({ vehicle_id: id }),
        api.getVehicleDocuments(id),
        api.getInspections(id),
      ]);
      setVehicle(vehicleData);
      setCosts(costsData);
      setDocuments(docsData);
      setInspections(inspData);
    } catch (error) {
      toast.error('Failed to load vehicle details');
      navigate('/vehicles');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteCost = async () => {
    if (!selectedCost) return;
    
    try {
      await api.deleteCost(selectedCost.id);
      toast.success('Cost entry deleted');
      setDeleteDialogOpen(false);
      setSelectedCost(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete cost entry');
    }
  };

  const handleMarkServiced = async () => {
    if (!serviceOdometer) {
      toast.error('Please enter the odometer reading');
      return;
    }

    setMarkingServiced(true);
    try {
      await api.markVehicleServiced(id, parseFloat(serviceOdometer));
      toast.success('Vehicle marked as serviced');
      setServiceDialogOpen(false);
      setServiceOdometer('');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark vehicle as serviced');
    } finally {
      setMarkingServiced(false);
    }
  };

  const handleToggleFleetAvg = async (nextIncluded) => {
    if (!vehicle) return;
    // Optimistic update
    setVehicle({ ...vehicle, include_in_fleet_avg: nextIncluded });
    try {
      await api.updateVehicle(vehicle.id, { include_in_fleet_avg: nextIncluded });
      toast.success(nextIncluded
        ? 'Included in fleet Km/L average'
        : 'Excluded from fleet Km/L average'
      );
    } catch (error) {
      setVehicle({ ...vehicle, include_in_fleet_avg: !nextIncluded });
      toast.error('Failed to update vehicle');
    }
  };

  const filteredCosts = costFilter === 'all' 
    ? costs 
    : costs.filter(c => c.cost_type === costFilter);

  // --- Document Handlers ---
  // Calculate stats
  const totalCost = costs.reduce((sum, c) => sum + c.amount, 0);
  const fuelCost = costs.filter(c => c.cost_type === 'fuel').reduce((sum, c) => sum + c.amount, 0);
  const serviceCost = costs.filter(c => c.cost_type === 'service').reduce((sum, c) => sum + c.amount, 0);
  const tyreCost = costs.filter(c => c.cost_type === 'tyres').reduce((sum, c) => sum + c.amount, 0);
  const totalLiters = costs.filter(c => c.cost_type === 'fuel').reduce((sum, c) => sum + (c.liters || 0), 0);

  // Calculate per-entry fuel efficiency for fuel entries only.
  // Applies the same outlier filter as the backend (services/fuel_efficiency.py)
  // so the Vehicle Detail page never displays nonsense numbers caused by
  // odometer typos, missing liters, or forklift km-vs-hours mistakes.
  // Sorted strictly by date input order.
  const isHourMetered = vehicle?.vehicle_type === 'forklift';

  const fuelEntries = costs
    .filter(c => c.cost_type === 'fuel' && c.liters && (isHourMetered ? c.hours != null : c.odometer))
    .sort((a, b) => a.date.localeCompare(b.date));

  const fuelEfficiencyMap = {};
  let totalFuelKm = 0, totalFuelLiters = 0;
  let totalFuelHours = 0;
  for (let i = 1; i < fuelEntries.length; i++) {
    const litersUsed = fuelEntries[i].liters;
    if (!(litersUsed > 0)) continue;
    if (isHourMetered) {
      const dh = fuelEntries[i].hours - fuelEntries[i - 1].hours;
      if (!(dh > 0)) continue;
      if (dh > 200) continue;                    // single-tank cap for forklifts
      const lph = litersUsed / dh;
      if (lph > 40) continue;                    // physical upper bound
      fuelEfficiencyMap[fuelEntries[i].id] = { lph: parseFloat(lph.toFixed(3)), hours: dh };
      totalFuelHours += dh;
      totalFuelLiters += litersUsed;
    } else {
      const kmDriven = fuelEntries[i].odometer - fuelEntries[i - 1].odometer;
      if (!(kmDriven > 0)) continue;
      if (kmDriven > 2000) continue;              // single tank <= 2000 km
      const kml = kmDriven / litersUsed;
      if (kml >= 30) continue;                    // physical upper bound
      fuelEfficiencyMap[fuelEntries[i].id] = { kml: parseFloat(kml.toFixed(2)), km: kmDriven };
      totalFuelKm += kmDriven;
      totalFuelLiters += litersUsed;
    }
  }
  const avgKmL = !isHourMetered && totalFuelKm > 0 && totalFuelLiters > 0
    ? (totalFuelKm / totalFuelLiters)
    : null;
  const avgLperHr = isHourMetered && totalFuelHours > 0 && totalFuelLiters > 0
    ? (totalFuelLiters / totalFuelHours)
    : null;
  const costPerKm = totalFuelKm > 0 ? (fuelCost / totalFuelKm) : null;
  const costPerHour = totalFuelHours > 0 ? (fuelCost / totalFuelHours) : null;

  // Monthly breakdown for chart
  const monthlyData = {};
  costs.forEach(c => {
    const month = c.date.slice(0, 7);
    monthlyData[month] = (monthlyData[month] || 0) + c.amount;
  });
  const chartData = Object.entries(monthlyData)
    .map(([month, amount]) => ({ month, amount }))
    .sort((a, b) => a.month.localeCompare(b.month))
    .slice(-6);

  const vehiclesArray = useMemo(() => vehicle ? [vehicle] : [], [vehicle]);
  const isHoursVehicle = ['hours', 'hour'].includes(vehicle?.usage_unit);
  const nextServiceReading = isHoursVehicle
    ? (vehicle?.next_service_hours || (vehicle?.last_service_hours ? vehicle.last_service_hours + (vehicle.service_interval_hours || 500) : null))
    : (vehicle?.next_service_km || (vehicle?.last_service_odometer ? vehicle.last_service_odometer + (vehicle.service_interval_km || 10000) : null));


  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="h-32 bg-slate-200 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="vehicle-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/vehicles">
            <Button variant="ghost" size="icon" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl lg:text-3xl font-heading font-bold text-slate-900 tracking-tight">
                {vehicle?.name}
              </h1>
              {vehicle?.service_due && (
                <Badge variant="destructive" className="bg-orange-500 text-white">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Service Due
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-slate-500">
              <span className="font-data">{vehicle?.registration}</span>
              <Badge variant="secondary" className="capitalize">
                {vehicle?.vehicle_type}
              </Badge>
              {isHoursVehicle && vehicle?.current_hours ? (
                <span className="font-data" data-testid="vehicle-current-hours">{vehicle.current_hours.toLocaleString()} hrs</span>
              ) : vehicle?.current_odometer && (
                <span className="font-data">{vehicle.current_odometer.toLocaleString()} km</span>
              )}
            </div>
            {vehicle && (
              <div
                className="flex items-center gap-2 mt-2"
                title="Untick to exclude this vehicle (e.g., a private car) from the group Km/L average"
              >
                <Checkbox
                  id="include-in-fleet-avg-detail"
                  checked={vehicle.include_in_fleet_avg !== false}
                  onCheckedChange={(checked) => handleToggleFleetAvg(checked === true)}
                  data-testid="include-in-fleet-avg-detail-checkbox"
                />
                <Label htmlFor="include-in-fleet-avg-detail" className="text-xs text-slate-600 cursor-pointer">
                  Include in fleet Km/L average
                </Label>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {vehicle?.is_sold ? (
            <>
              <span className="px-3 py-1 rounded-full bg-slate-200 text-slate-700 text-sm font-semibold" data-testid="sold-badge">
                SOLD · {vehicle.sold_date}
              </span>
              <Button variant="outline" onClick={unmarkSold} data-testid="unmark-sold-btn">
                Return to active
              </Button>
            </>
          ) : (
            <>
              {vehicle?.service_due && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setServiceOdometer(vehicle?.current_odometer?.toString() || '');
                    setServiceDialogOpen(true);
                  }}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                  data-testid="mark-serviced-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Serviced
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setSoldDate(new Date().toISOString().slice(0, 10));
                  setSoldDialogOpen(true);
                }}
                className="border-slate-300 text-slate-700 hover:bg-slate-100"
                data-testid="mark-sold-btn"
              >
                Mark as Sold
              </Button>
              <Button 
                onClick={() => setCostDialogOpen(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white btn-active"
                data-testid="add-cost-vehicle-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Cost
              </Button>
            </>
          )}
        </div>
      </div>

      {vehicle?.is_sold && (
        <div className="rounded-md border border-slate-300 bg-slate-100 p-4 flex items-start gap-3" data-testid="sold-banner">
          <div className="w-10 h-10 rounded-full bg-slate-300 flex items-center justify-center text-slate-700 font-bold">S</div>
          <div className="flex-1 text-sm text-slate-700">
            <p><strong>This vehicle was sold on {vehicle.sold_date}</strong>. It no longer appears in Cost Budget forecasts, service / licence / insurance alerts, dashboard KPIs or fleet averages.</p>
            <p className="text-xs text-slate-500 mt-1">
              Historical costs stay intact. Backdated entries dated on or before <strong>{vehicle.sold_date}</strong> are still accepted.
              {vehicle.sold_price != null && <> · Sold for <strong>R {Number(vehicle.sold_price).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</strong></>}
              {vehicle.sold_buyer && <> · Buyer: <strong>{vehicle.sold_buyer}</strong></>}
            </p>
          </div>
        </div>
      )}

      {/* Service Info Card */}
      {(vehicle?.next_service_km || vehicle?.last_service_date) && (
        <Card className={vehicle?.service_due ? 'border-orange-300 bg-orange-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  vehicle?.service_due ? 'bg-orange-500' : 'bg-slate-100'
                }`}>
                  <Wrench className={`w-6 h-6 ${vehicle?.service_due ? 'text-white' : 'text-slate-600'}`} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">Service Schedule</h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm" data-testid="service-schedule-summary">
                    {/* hours-based assets (forklifts) track engine hours instead of km */}
                    {vehicle?.last_service_date && (
                      <span className="text-slate-500">
                        Last service: <span className="font-data">{vehicle.last_service_date}</span>
                        {isHoursVehicle
                          ? (vehicle?.last_service_hours ? <span> at {vehicle.last_service_hours.toLocaleString()} hrs</span> : null)
                          : (vehicle?.last_service_odometer ? <span> at {vehicle.last_service_odometer.toLocaleString()} km</span> : null)}
                      </span>
                    )}
                    {nextServiceReading && (
                      <span className={vehicle?.service_due ? 'text-orange-700 font-medium' : 'text-slate-500'} data-testid="next-service-reading">
                        Next service: <span className="font-data">
                          {`${nextServiceReading.toLocaleString()} ${isHoursVehicle ? 'hrs' : 'km'}`}
                        </span>
                      </span>
                    )}
                    {vehicle?.next_service_expected_date && (
                      <span className={vehicle?.service_due ? 'text-orange-700 font-medium' : 'text-slate-500'} data-testid="next-service-date">
                        Expected: <span className="font-data">{vehicle.next_service_expected_date}</span>
                      </span>
                    )}
                    <span className="text-slate-400">
                      Interval: {isHoursVehicle
                        ? `${(vehicle?.service_interval_hours || 500).toLocaleString()} hrs`
                        : `${(vehicle?.service_interval_km || 10000).toLocaleString()} km`}
                    </span>
                  </div>
                </div>
              </div>
              {!vehicle?.service_due && (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setServiceOdometer(vehicle?.current_odometer?.toString() || '');
                    setServiceDialogOpen(true);
                  }}
                  data-testid="record-service-btn"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Record Service
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <p className="overline">Total Cost</p>
            <p className="text-xl font-heading font-bold text-slate-900 mt-1 data-value">{fmtR(totalCost)}</p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <p className="overline">Fuel Cost</p>
            <p className="text-xl font-heading font-bold text-orange-500 mt-1 data-value">{fmtR(fuelCost)}</p>
          </CardContent>
        </Card>
        <Card className={`card-hover ${
          isHourMetered
            ? (avgLperHr ? (avgLperHr <= 2 ? 'border-green-200 bg-green-50/50' : avgLperHr <= 4 ? 'border-amber-200 bg-amber-50/50' : 'border-red-200 bg-red-50/50') : '')
            : (avgKmL ? (avgKmL >= 10 ? 'border-green-200 bg-green-50/50' : avgKmL >= 7 ? 'border-amber-200 bg-amber-50/50' : 'border-red-200 bg-red-50/50') : '')
        }`}>
          <CardContent className="p-4">
            <p className="overline">{isHourMetered ? 'Avg L/hr' : 'Avg km/L'}</p>
            <p
              className={`text-xl font-heading font-bold mt-1 data-value ${
                isHourMetered
                  ? (avgLperHr ? (avgLperHr <= 2 ? 'text-green-600' : avgLperHr <= 4 ? 'text-amber-600' : 'text-red-600') : 'text-slate-400')
                  : (avgKmL ? (avgKmL >= 10 ? 'text-green-600' : avgKmL >= 7 ? 'text-amber-600' : 'text-red-600') : 'text-slate-400')
              }`}
              data-testid="avg-kml-stat"
            >
              {isHourMetered
                ? (avgLperHr ? `${avgLperHr.toFixed(2)} L/hr` : '--')
                : (avgKmL ? `${avgKmL.toFixed(2)} km/L` : '--')}
            </p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <p className="overline">{isHourMetered ? 'Cost / hour' : 'Cost / km'}</p>
            <p className="text-xl font-heading font-bold text-blue-500 mt-1 data-value" data-testid="cost-per-km-stat">
              {isHourMetered
                ? (costPerHour ? fmtR(costPerHour) : '--')
                : (costPerKm ? fmtR(costPerKm) : '--')}
            </p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <p className="overline">Total Fuel</p>
            <p className="text-xl font-heading font-bold text-slate-900 mt-1 data-value">{totalLiters.toFixed(1)} L</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { key: 'costs', label: 'Cost History', icon: Fuel },
          { key: 'scorecard', label: 'Driver Scorecard', icon: Star },
          { key: 'documents', label: `Documents${documents.length ? ` (${documents.length})` : ''}`, icon: FileText },
          { key: 'inspections', label: `Inspections${inspections.length ? ` (${inspections.length})` : ''}`, icon: ClipboardCheck },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key
                ? 'border-gray-800 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB: Cost History */}
      {activeTab === 'costs' && (
        <VehicleCostsTab
          chartData={chartData}
          filteredCosts={filteredCosts}
          costFilter={costFilter}
          setCostFilter={setCostFilter}
          fuelEfficiencyMap={fuelEfficiencyMap}
          setCostDialogOpen={setCostDialogOpen}
          setSelectedCost={setSelectedCost}
          setEditCostDialogOpen={setEditCostDialogOpen}
          setDeleteDialogOpen={setDeleteDialogOpen}
        />
      )}

      {/* TAB: Driver Scorecard */}
      {activeTab === 'scorecard' && vehicle && (
        <DriverScorecard vehicleId={vehicle.id} vehicleName={`${vehicle.name} (${vehicle.registration})`} />
      )}

      {/* TAB: Documents */}
      {activeTab === 'documents' && (
        <VehicleDocumentsTab
          vehicleId={id}
          documents={documents}
          setDocuments={setDocuments}
          onRefresh={fetchData}
        />
      )}

      {/* TAB: Inspections */}
      {activeTab === 'inspections' && vehicle && (
        <VehicleInspectionsTab
          vehicleId={id}
          vehicle={vehicle}
          inspections={inspections}
          setInspections={setInspections}
          onRefresh={fetchData}
        />
      )}

      {/* ====== DIALOGS ====== */}
      <AddCostDialog open={costDialogOpen} onOpenChange={setCostDialogOpen} vehicles={vehiclesArray} defaultVehicleId={vehicle?.id} onSuccess={fetchData} />
      <EditCostDialog open={editCostDialogOpen} onOpenChange={setEditCostDialogOpen} cost={selectedCost} onSuccess={fetchData} />

      {/* Mark Serviced */}
      <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Service</DialogTitle>
            <DialogDescription>Enter the odometer reading when the service was performed</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Odometer Reading (km) *</Label>
              <Input type="number" value={serviceOdometer} onChange={(e) => setServiceOdometer(e.target.value)} placeholder="e.g., 55000" className="font-data" data-testid="service-odometer-input" />
              {vehicle?.next_service_km && <p className="text-sm text-slate-500">Next service was due at {vehicle.next_service_km.toLocaleString()} km</p>}
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setServiceDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleMarkServiced} className="bg-slate-900 hover:bg-slate-800" disabled={markingServiced} data-testid="confirm-service-btn">{markingServiced ? 'Saving...' : 'Mark as Serviced'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark as Sold */}
      <Dialog open={soldDialogOpen} onOpenChange={setSoldDialogOpen}>
        <DialogContent className="max-w-md" data-testid="mark-sold-dialog">
          <DialogHeader>
            <DialogTitle>Mark {vehicle?.name} as sold</DialogTitle>
            <DialogDescription asChild>
              <p className="text-sm text-slate-600">
                All historical data stays intact. Future costs on or after the sold date will be rejected, and this vehicle disappears from forward-looking alerts, forecasts and KPIs.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label>Sold date *</Label>
              <Input type="date" value={soldDate} onChange={(e) => setSoldDate(e.target.value)} data-testid="sold-date-input" />
            </div>
            <div className="space-y-1">
              <Label>Sale price (R) <span className="text-slate-400 font-normal">— optional</span></Label>
              <Input type="number" step="0.01" value={soldPrice} onChange={(e) => setSoldPrice(e.target.value)} placeholder="e.g. 180000" data-testid="sold-price-input" />
            </div>
            <div className="space-y-1">
              <Label>Buyer <span className="text-slate-400 font-normal">— optional</span></Label>
              <Input value={soldBuyer} onChange={(e) => setSoldBuyer(e.target.value)} placeholder="e.g. Jones Logistics" data-testid="sold-buyer-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setSoldDialogOpen(false)}>Cancel</Button>
            <Button onClick={markSold} className="bg-slate-900 hover:bg-slate-800" disabled={savingSold} data-testid="confirm-sold-btn">
              {savingSold ? 'Saving…' : 'Mark as Sold'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Cost */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Cost Entry</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this cost entry? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCost} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-cost-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VehicleDetailPage;
